/**
 * TriageStream (TRIAGE-01, Seth 2026-06-11: "let's try a new design… every
 * alert needs an x") — ONE prioritized morning list instead of stacked
 * panels. Every row: severity icon, type chip, who + what in plain words,
 * ONE primary click (peek the dispatch in place / Qualify the customer —
 * never a surprise new tab), and an ✕ that clears it for the whole office
 * (persisted in pegasus_alert_dismissal by stable key). Follow-ups keep
 * their own lifecycle: ✓ completes, ✕ snoozes to tomorrow.
 *
 * Sources merged: tech-assignment conflicts, reminder bounces, tomorrow's
 * no-email + window mismatches, follow-ups due, new queue tickets (24h),
 * parts arrived. On-call overnight keeps its own richer panel below.
 */
import { useMemo, useState } from "react";
import {
  AlertTriangle,
  BellRing,
  CalendarPlus,
  CheckCircle2,
  Clock,
  Mail,
  Package,
  UserX,
  X,
  Check,
  Clock3,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import { OverflowMenu } from "@/components/ui/Menu";
import { CustomerLink } from "@/components/links";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { displayCustomerName } from "@/lib/format";
import { showOnBoard } from "@/lib/boardChannel";
import { useDismissals, useDismiss, useDismissMany } from "./dismissals";
import { useOrgSettingJson } from "@/lib/orgSettings";
import { DispatchPeek } from "@/features/customer-360/DispatchPeek";
import {
  useRecentReminders,
  useTomorrowReminderCandidates,
  computeTomorrowQualityIssues,
} from "./api";
import { useBoardConflicts } from "./conflicts";
import { Link } from "react-router-dom";
import { usePendingTickets, useUpdateTicket, type PartState } from "@/features/board/queue";
import { useAllActiveTechs } from "@/features/board/api";
import type { PartEquipment } from "@/features/purchase-orders/po";
import {
  useDueFollowups,
  useCompleteFollowup,
  useSnoozeFollowup,
  daysFromNowISO,
  dueLabel,
  type FollowUp,
} from "@/features/followups/followups";

type Tone = "danger" | "warning" | "info" | "task";

/** TODAY-V3 (Seth 2026-08-19: "breakdown sections by agreements, after hours,
 *  installs, service… board / routing issues"): every row carries its KIND so
 *  the page can render domain SECTIONS — each section is its own TriageStream
 *  slice with its own clear-all, same rows, same dismissal keys. */
export type TriageKind =
  | "conflict" | "bounce" | "mismatch" | "noemail" | "looseend"
  | "parts" | "newticket" | "fu_agreement" | "fu_manual";

interface TriageRow {
  key: string; // stable identity — also the dismissal key
  kind: TriageKind;
  rank: number; // sort order (lower = higher priority)
  tone: Tone;
  icon: React.ComponentType<{ className?: string }>;
  chip: string; // type chip text ("Wrong tech", "Bounce", "Follow-up"…)
  customer?: { custno: string; locno?: string | null; name: string };
  /** What happened / what to do, plain words. */
  line: string;
  meta?: string; // small right-side context (day, age, due)
  dispatch?: string | null; // click → peek
  followup?: FollowUp; // follow-up rows get ✓ + snooze instead of plain ✕
  boardDay?: string; // conflicts: secondary "Show on board"
  /** A number to call — the row's action button when the fix is a call. */
  phone?: string | null;
  /** PARTS-DESK (Seth 2026-08-28): part tickets carry their workflow — the
   *  same Order (PO) / Ordered / In stock / Arrived actions as the board strip. */
  ticket?: { id: string; state: PartState; origin: string | null; custno: string | null; locno: string | null; part: string | null; summary: string; equip?: PartEquipment | null };
}

const TONE_STYLES: Record<Tone, { chip: string; icon: string }> = {
  danger: { chip: "bg-red-50 text-red-700", icon: "text-red-500" },
  warning: { chip: "bg-amber-50 text-amber-800", icon: "text-amber-500" },
  info: { chip: "bg-sky-50 text-sky-700", icon: "text-sky-500" },
  task: { chip: "bg-brand-50 text-brand-700", icon: "text-brand-500" },
};

/** Yesterday's dispatches that never reached a done status — the morning
 *  close-out hygiene list (forgotten closes delay billing + rot the data
 *  every report stands on). Replaces the "pointless" WeekStrip (Seth
 *  2026-06-11) with something a dispatcher acts on. */
function useYesterdayLooseEnds() {
  return useQuery({
    queryKey: ["yesterday_loose_ends"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const yISO = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const { data, error } = await supabase
        .from("v_dispatch_full")
        .select("dispatch, custno, locno, locname, customer_fullname, city, status, tech_name, tech_first, tech_last, tech_empno")
        .eq("appointment_date", yISO)
        .limit(500);
      if (error) throw error;
      const DONE = new Set(["complete", "done", "cancelled", "canceled", "cancel"]);
      // ESC's pseudo-employee holding buckets (Serv Agr / Parts / Sales /
      // Reschedule…) are parked-on-purpose, not forgotten closes — exclude
      // ([[reference_pseudo_employees]]).
      const PSEUDO = new Set(["7777", "9999", "8888", "1002", "1003", "0000", "0001", "1000", "1001"]);
      const rows = (data ?? []) as {
        dispatch: string; custno: string; locno: string | null; locname: string | null;
        customer_fullname: string | null; city: string | null; status: string | null;
        tech_name: string | null; tech_first: string | null; tech_last: string | null; tech_empno: string;
      }[];
      return {
        yISO,
        rows: rows.filter(
          (r) =>
            !DONE.has((r.status ?? "pending").toLowerCase()) &&
            !PSEUDO.has(r.tech_empno),
        ),
      };
    },
  });
}

/** TODAY-REDESIGN-02: the row ASSEMBLY, extracted so the Desk's queue tabs
 *  can count exactly what each TriageStream slice will render (same React
 *  Query keys underneath — the fetches dedupe, only the memo re-runs). */
export function useTriageRows(include?: TriageKind[]): { rows: TriageRow[]; loading: boolean } {
  const conflicts = useBoardConflicts();
  const reminders = useRecentReminders();
  const tomorrow = useTomorrowReminderCandidates();
  // REACH-01: with SMS reminders on, a no-email customer with a number gets
  // a text automatically — those rows don't exist.
  const smsRem = useOrgSettingJson<{ enabled: boolean; mode: string }>("reminder_sms", { enabled: false, mode: "no_email_only" });
  const followups = useDueFollowups();
  const techNames = useAllActiveTechs().data;
  const tickets = usePendingTickets();
  const looseEnds = useYesterdayLooseEnds();
  const dismissals = useDismissals();

  // Names for follow-up custnos (small keyed batch).
  const fuCustnos = [...new Set((followups.data ?? []).map((f) => f.custno))];
  const { data: fuNames } = useQuery({
    queryKey: ["triage_fu_names", fuCustnos.join(",")],
    enabled: fuCustnos.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("mirror_customer")
        .select("custno, fullname")
        .in("custno", fuCustnos);
      return new Map(
        ((data ?? []) as { custno: string; fullname: string | null }[]).map((r) => [
          r.custno,
          displayCustomerName(null, r.fullname),
        ]),
      );
    },
  });

  const rows = useMemo<TriageRow[]>(() => {
    const out: TriageRow[] = [];

    // 1 — tech-assignment conflicts (the dispatch is WRONG right now).
    for (const c of conflicts.data ?? []) {
      out.push({
        key: `conflict:${c.dispatch}:${c.dayISO}:${c.kind}`,
        kind: "conflict",
        rank: c.dayLabel === "today" ? 0 : 1,
        tone: "danger",
        icon: UserX,
        chip: c.kind === "excluded" ? "Don't-send conflict" : c.kind === "out" ? "Tech is out" : c.kind === "requested" ? "Wrong tech" : "Skill flag",
        customer: { custno: c.custno, name: c.customer },
        line: c.line,
        meta: c.dayLabel,
        dispatch: c.dispatch,
        boardDay: c.dayISO,
      });
    }

    // 2 — reminder bounces (the customer didn't get our message).
    for (const r of (reminders.data ?? []).filter(
      (r) => r.status === "bounced" || r.status === "failed",
    )) {
      out.push({
        key: `bounce:${r.recipient ?? "?"}:${r.dispatch ?? r.sent_at ?? "?"}`,
        kind: "bounce",
        rank: 2,
        tone: "danger",
        icon: AlertTriangle,
        chip: "Reminder bounced",
        line: `${r.customer_name ?? r.recipient ?? "Unknown"} — ${r.recipient ?? "no address"}${r.status_detail ? ` · ${r.status_detail}` : ""}`,
        dispatch: r.dispatch ?? null,
      });
    }

    // 3 — follow-ups due (the office's promises — and the techs' own: a
    // follow-up a tech is handling still shows here, so nothing hides on one phone).
    for (const f of followups.data ?? []) {
      const dl = dueLabel(f);
      const who = f.assigned_empno ? (techNames ?? []).find((t) => t.empno === f.assigned_empno) : null;
      const whoName = who ? ((who.firstname ?? "").trim() || (who.empname ?? "").trim().split(/\s+/)[0] || f.assigned_empno) : null;
      out.push({
        key: `followup:${f.id}`,
        kind: f.source === "agreement" ? "fu_agreement" : "fu_manual",
        rank: dl.tone === "overdue" ? 3 : 4,
        tone: "task",
        icon: BellRing,
        chip: f.source === "agreement" ? "Renewal call" : whoName ? `${whoName}'s follow-up` : "Follow-up",
        customer: { custno: f.custno, locno: f.locno, name: fuNames?.get(f.custno) ?? f.custno },
        line: f.title + (f.details ? ` — ${f.details}` : ""),
        meta: [dl.text, whoName ? `${whoName} handles it` : null, f.repeat_months ? `repeats every ${f.repeat_months === 12 ? "year" : `${f.repeat_months} mo`}` : null].filter(Boolean).join(" · "),
        followup: f,
      });
    }

    // 4 — tomorrow's send-quality problems.
    const { noEmail, mismatch } = computeTomorrowQualityIssues(
      (tomorrow.data ?? []) as Record<string, unknown>[],
      { smsReminders: smsRem.data?.enabled === true },
    );
    for (const it of mismatch) {
      out.push({
        key: `mismatch:${it.dispatch}`,
        kind: "mismatch",
        rank: 5,
        tone: "warning",
        icon: Clock,
        chip: "Window mismatch",
        customer: { custno: it.custno, locno: it.locno, name: it.customer },
        line: `Priority says ${it.priWin}, notes say ${it.notesWin} — tomorrow`,
        dispatch: it.dispatch,
      });
    }
    for (const it of noEmail) {
      // REACH-01: two honest flavors — "truly unreachable" (no email, no
      // number) vs "no reminder going out" (has a number, but texting is
      // off so nothing sends itself).
      const unreachable = it.reachable === "none";
      out.push({
        key: `noemail:${it.dispatch}`,
        kind: "noemail",
        rank: unreachable ? 5.5 : 6,
        tone: "warning",
        icon: Mail,
        chip: unreachable ? "Unreachable" : "No reminder going out",
        customer: { custno: it.custno, locno: it.locno, name: it.customer },
        line: unreachable
          ? "No email and no phone on file — tomorrow's reminder can't reach them"
          : "No email on file and texting is off — nobody reminds them unless you call",
        dispatch: it.dispatch,
        phone: it.phone ?? null,
      });
    }

    // 4b — yesterday's loose ends: stops that never reached a done status.
    for (const r of looseEnds.data?.rows ?? []) {
      const tech =
        r.tech_name ?? (`${r.tech_first ?? ""} ${r.tech_last ?? ""}`.trim() || r.tech_empno);
      out.push({
        key: `looseend:${r.dispatch}:${r.tech_empno}:${looseEnds.data?.yISO}`,
        kind: "looseend",
        rank: 6.5,
        tone: "warning",
        icon: Clock,
        chip: "Still open",
        customer: { custno: r.custno, locno: r.locno, name: displayCustomerName(r.locname, r.customer_fullname) },
        line: `Yesterday's stop never closed — ${tech} shows "${r.status ?? "Pending"}"`,
        meta: r.city ?? undefined,
        dispatch: r.dispatch,
      });
    }

    // 5 — queue movement: part tickets (their WHOLE lifecycle) + new tickets (24h).
    const dayAgo = Date.now() - 24 * 3600 * 1000;
    for (const t of tickets.data ?? []) {
      if (t.lane_key === "missed_call") continue; // its own lifecycle on the board strip
      // PARTS-DESK (Seth 2026-08-28): a part ticket is NEVER a "New request" —
      // it renders by its part_state, with the ordering actions right here, and
      // an ordered ticket stops shouting (his "still sitting as pending" bug).
      if (t.lane_key === "order_parts") {
        const cust = t.custno ? { custno: t.custno, locno: t.locno, name: displayCustomerName(null, t.customer_name) } : undefined;
        const tk = { id: t.id, state: (t.part_state ?? "needs_ordering") as PartState, origin: t.origin_dispatch, custno: t.custno, locno: t.locno, part: t.part_number, summary: t.summary, equip: t.part_equipment ?? null };
        // TICKET-EQUIP-01: the unit rides on the ticket as data (model + serial);
        // older flags only baked it into the notes ("Unit: Lennox EL296V · S/N …").
        const eq = t.part_equipment;
        const unit = eq
          ? `${[eq.mfg, eq.model].filter(Boolean).join(" ") || eq.eq_type || "unit on file"}${eq.serial ? ` · S/N ${eq.serial}` : ""}`
          : ((t.notes ?? "").match(/Unit:\s*([^·]+)/)?.[1] ?? "").trim() || null;
        const detail = `${t.summary}${t.part_number ? ` · #${t.part_number}` : ""}${unit ? ` — for the ${unit}` : ""}`;
        if (t.part_state === "arrived" || t.part_state === "in_stock") {
          out.push({
            key: `parts:${t.id}`, kind: "parts", rank: 7, tone: "info", icon: Package,
            chip: t.part_state === "in_stock" ? "Part in stock" : "Part arrived",
            customer: cust, line: `${t.summary} — book the return visit`, meta: t.city ?? undefined,
          });
        } else if (t.part_state === "ordered") {
          out.push({
            key: `parts_ordered:${t.id}`, kind: "parts", rank: 9, tone: "info", icon: Package,
            chip: t.part_po ? `On order · PO ${t.part_po}` : "On order",
            customer: cust, line: `${detail} — waiting on the part`,
            meta: t.part_eta ? `ETA ${t.part_eta}` : undefined, ticket: tk,
          });
        } else {
          const critical = t.part_priority === "critical";
          out.push({
            key: `parts_need:${t.id}`, kind: "parts", rank: critical ? 3.0 : 4.2, tone: "danger", icon: Package,
            chip: critical ? "CRITICAL — no heat/cool" : t.part_state === "backordered" ? "Backordered" : "Part needs ordering",
            customer: cust, line: detail, meta: t.city ?? undefined, ticket: tk,
          });
        }
        continue;
      }
      if (new Date(t.created_at).getTime() >= dayAgo) {
        out.push({
          key: `newticket:${t.id}`,
          kind: "newticket",
          rank: 8,
          tone: "info",
          icon: CalendarPlus,
          chip: "New request",
          customer: t.custno ? { custno: t.custno, locno: t.locno, name: displayCustomerName(null, t.customer_name) } : undefined,
          line: t.summary,
          meta: t.city ?? undefined,
        });
      }
    }

    const dismissed = dismissals.data ?? new Set<string>();
    const wanted = include ? new Set(include) : null;
    return out
      .filter((r) => !dismissed.has(r.key))
      .filter((r) => (wanted ? wanted.has(r.kind) : true))
      .sort((a, b) => a.rank - b.rank);
  }, [conflicts.data, reminders.data, followups.data, tomorrow.data, tickets.data, looseEnds.data, dismissals.data, fuNames, include, techNames, smsRem.data]);

  const loading =
    conflicts.isLoading || reminders.isLoading || tomorrow.isLoading ||
    followups.isLoading || tickets.isLoading;

  return { rows, loading };
}

export function TriageStream({
  include,
  emptyCard = false,
}: {
  /** Which row kinds this instance renders (omit = everything). */
  include?: TriageKind[];
  /** Show the green "all clear" card when empty (the board section wants it;
   *  other sections self-hide by returning null). */
  emptyCard?: boolean;
} = {}) {
  const { rows, loading } = useTriageRows(include);
  const dismiss = useDismiss();
  const dismissMany = useDismissMany();
  const completeFu = useCompleteFollowup();
  const updateTicket = useUpdateTicket();
  const snoozeFu = useSnoozeFollowup();
  const [peek, setPeek] = useState<string | null>(null);
  // "Clear all" is office-wide, so it asks twice: first click arms, second
  // click clears. Blur disarms.
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmSnooze, setConfirmSnooze] = useState(false);
  const [snoozing, setSnoozing] = useState(false);
  // The renewal generator's first run made the office's real backlog visible
  // all at once — keep the morning workable: 8 follow-ups visible, the rest
  // behind "Show all" (alerts/conflicts are never capped).
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [kindFilter, setKindFilter] = useState<"all" | "parts" | "fu" | "alerts">("all");

  // Conflicts: secondary action — jump the AMBIENT board window; falls back
  // to REUSING the one named board tab, never piling new ones (boardChannel).

  if (loading && rows.length === 0) {
    return emptyCard ? <Card className="h-12 animate-pulse bg-slate-100" /> : null;
  }

  if (rows.length === 0) {
    if (!emptyCard) return null;
    // DESIGN-CALM-01: a quiet section is one line, not a paragraph.
    return (
      <Card className="flex items-center gap-2.5 px-4 py-2.5">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <span className="text-sm text-slate-600">All clear.</span>
      </Card>
    );
  }

  // Cap follow-ups at 8 unless expanded (alerts always show).
  const totalFollowups = rows.filter((r) => r.followup).length;
  let fuSeen = 0;
  const visible = rows.filter((r) => {
    // PO-02 (Seth 2026-08-31: "worried about long lists forming"): quick kind
    // filter — Parts / Follow-ups / Alerts — so the parts desk can see JUST
    // its pile.
    if (kindFilter === "parts" && r.kind !== "parts") return false;
    if (kindFilter === "fu" && !r.followup) return false;
    if (kindFilter === "alerts" && (r.kind === "parts" || r.followup)) return false;
    if (!r.followup) return true;
    fuSeen += 1;
    return showAllTasks || fuSeen <= 8;
  });
  const hiddenFu = totalFollowups - Math.min(totalFollowups, 8);

  // "Clear all" sweeps ALERTS only — follow-ups are promises with their own
  // ✓/snooze lifecycle and never get bulk-dismissed. But they CAN bulk-defer
  // (Seth 2026-08-19: "I don't want to have to manually click all of these")
  // — "Snooze all" pushes every follow-up in this section to tomorrow.
  const alertKeys = rows.filter((r) => !r.followup).map((r) => r.key);
  const fuRows = rows.filter((r) => r.followup);
  const snoozeAll = async () => {
    setSnoozing(true);
    try {
      const until = daysFromNowISO(1);
      await Promise.all(fuRows.map((r) => snoozeFu.mutateAsync({ id: r.followup!.id, until })));
    } finally {
      setSnoozing(false);
      setConfirmSnooze(false);
    }
  };

  return (
    <>
      <Card className="divide-y divide-slate-100 overflow-hidden">
        {rows.length > 0 && (
          <div className="flex items-center justify-between bg-slate-50/60 px-4 py-1.5">
            <span className="text-[11px] font-medium text-slate-500">
              {alertKeys.length > 0 && `${alertKeys.length} alert${alertKeys.length === 1 ? "" : "s"}`}
              {alertKeys.length > 0 && totalFollowups > 0 && " · "}
              {totalFollowups > 0 && `${totalFollowups} to work — ✓ completes, snooze pushes to tomorrow`}
            </span>
            <span className="flex items-center gap-1.5">
            {alertKeys.length > 0 && (
            <button
              type="button"
              disabled={dismissMany.isPending}
              onClick={() => {
                if (!confirmClear) {
                  setConfirmClear(true);
                  return;
                }
                dismissMany.mutate(alertKeys, { onSettled: () => setConfirmClear(false) });
              }}
              onBlur={() => setConfirmClear(false)}
              title="Clears every alert above for the whole office — follow-ups stay"
              className={cn(
                "rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors",
                confirmClear
                  ? "bg-red-50 text-red-700 hover:bg-red-100"
                  : "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
              )}
            >
              {dismissMany.isPending
                ? "Clearing…"
                : confirmClear
                  ? `Clear ${alertKeys.length} for the whole office?`
                  : alertKeys.length === 1 ? "Clear the alert" : "Clear all alerts"}
            </button>
            )}
            {fuRows.length > 1 && (
            <button
              type="button"
              disabled={snoozing}
              onClick={() => {
                if (!confirmSnooze) { setConfirmSnooze(true); return; }
                void snoozeAll();
              }}
              onBlur={() => setConfirmSnooze(false)}
              title="Push every follow-up in this section to tomorrow — they all come back"
              className={cn(
                "rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors",
                confirmSnooze
                  ? "bg-amber-50 text-amber-800 hover:bg-amber-100"
                  : "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
              )}
            >
              {snoozing ? "Snoozing…" : confirmSnooze ? `Snooze all ${fuRows.length} to tomorrow?` : "Snooze all to tomorrow"}
            </button>
            )}
            </span>
          </div>
        )}
        {rows.length > 6 && (
          <div className="flex items-center gap-1 bg-white px-4 py-1.5">
            {([["all", "Everything"], ["parts", "Parts"], ["fu", "Follow-ups"], ["alerts", "Alerts"]] as const).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setKindFilter(k)}
                className={cn("rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors",
                  kindFilter === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200")}>
                {label}
                {k === "parts" && rows.some((r) => r.kind === "parts" && r.tone === "danger") && kindFilter !== "parts" && (
                  <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-red-500 align-middle" />
                )}
              </button>
            ))}
          </div>
        )}
        {visible.map((r) => {
          const t = TONE_STYLES[r.tone];
          const clickable = !!r.dispatch;
          const rowBtn = "inline-flex h-7 items-center rounded-md bg-brand-50 px-2.5 text-xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-100 transition-colors hover:bg-brand-100";
          const quietBtn = "inline-flex h-7 items-center rounded-md border border-hairline bg-white px-2.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50";
          return (
            <div
              key={r.key}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => setPeek(r.dispatch!) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setPeek(r.dispatch!);
                      }
                    }
                  : undefined
              }
              className={cn(
                "group flex items-center gap-3 px-4 py-3",
                clickable && "cursor-pointer transition-colors hover:bg-brand-50/40",
              )}
              title={clickable ? "Preview the dispatch without leaving this page" : undefined}
            >
              {/* DESIGN-CALM-01 Wave 2: the kind chip is the one colored thing on the
                  row (the icon that said the same thing twice is gone). */}
              <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold", t.chip)}>
                {r.chip}
              </span>
              <div className="min-w-0 flex-1">
                <span className="text-[13.5px] text-slate-700">
                  {r.customer && (
                    <>
                      <span onClick={(e) => e.stopPropagation()}>
                        <CustomerLink
                          custno={r.customer.custno}
                          locno={r.customer.locno ?? undefined}
                          tab={r.kind === "fu_agreement" ? "agreements" : undefined}
                          title={r.kind === "fu_agreement" ? "Open this customer's plan (Agreements tab)" : "Pull up this customer's record"}
                          className="text-[14px] font-bold"
                        >
                          {r.customer.name}
                        </CustomerLink>
                      </span>
                      {" — "}
                    </>
                  )}
                  {r.line}
                </span>
              </div>
              {r.meta && (
                <span className="shrink-0 text-xs text-slate-400">
                  {r.meta}
                </span>
              )}

              {/* Actions */}
              <div
                className="flex shrink-0 items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                {r.boardDay && (
                  <button
                    type="button"
                    onClick={() => showOnBoard(r.boardDay!)}
                    title="Jump your board window to this day (the move tools live there)"
                    className={quietBtn}
                  >
                    Show on board
                  </button>
                )}
                {r.phone && (
                  <a href={`tel:${r.phone.replace(/[^0-9+]/g, "")}`} title={`Call ${r.phone}`} className={quietBtn}>
                    Call
                  </a>
                )}
                {/* Parts: one action on the row, the other two under "…" (Wave 2 Row-actions rule). */}
                {r.ticket && (r.ticket.state === "needs_ordering" || r.ticket.state === "backordered") && (
                  <>
                    <Link
                      to={`/purchase-orders?new=1&back=today&ticket=${encodeURIComponent(r.ticket.id)}&dispatch=${encodeURIComponent(r.ticket.origin ?? "")}&custno=${encodeURIComponent(r.ticket.custno ?? "")}&locno=${encodeURIComponent(r.ticket.locno ?? "")}&part=${encodeURIComponent(r.ticket.part ?? "")}&desc=${encodeURIComponent(r.ticket.summary)}${r.ticket.equip ? `&equip=${encodeURIComponent(JSON.stringify(r.ticket.equip))}` : ""}`}
                      title="Start the purchase order prefilled from this ticket — creating it with this dispatch # flips the ticket to On order with the PO number attached"
                      className={rowBtn}
                    >
                      Order (PO)
                    </Link>
                    <OverflowMenu
                      label="More actions"
                      items={[
                        { label: "Mark ordered", title: "Ordered outside Pegasus (phone / vendor site) — attaches the PO number so it's trackable", onSelect: () => { const po = window.prompt("Ordered outside Pegasus — PO number to attach (leave blank if none):") ?? ""; updateTicket.mutate({ id: r.ticket!.id, part_state: "ordered", part_po: po.trim() || null }); } },
                        ...(r.ticket.state === "needs_ordering" ? [{ label: "In stock", title: "Already on the shelf — no ordering needed", onSelect: () => updateTicket.mutate({ id: r.ticket!.id, part_state: "in_stock" }) }] : []),
                      ]}
                    />
                  </>
                )}
                {r.ticket && r.ticket.state === "ordered" && (
                  <button
                    type="button"
                    onClick={() => updateTicket.mutate({ id: r.ticket!.id, part_state: "arrived" })}
                    title="It's in hand — flips to ready-to-book"
                    className={rowBtn}
                  >
                    Arrived
                  </button>
                )}
                {r.followup ? (
                  <>
                    <button
                      type="button"
                      onClick={() => completeFu.mutate(r.followup!)}
                      title="Done — clears it with your name on it"
                      className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 text-transparent transition-colors hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-600"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => snoozeFu.mutate({ id: r.followup!.id, until: daysFromNowISO(1) })}
                      title="Snooze to tomorrow — it comes back"
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    >
                      <Clock3 className="h-3.5 w-3.5" />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => dismiss.mutate(r.key)}
                    title="Clear this alert for the whole office"
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-300 transition-colors hover:bg-slate-100 hover:text-red-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {hiddenFu > 0 && !showAllTasks && (
          <button
            type="button"
            onClick={() => setShowAllTasks(true)}
            className="block w-full px-4 py-2 text-center text-xs font-medium text-brand-600 transition-colors hover:bg-brand-50/40 hover:text-brand-700"
          >
            Show all {totalFollowups} follow-ups ({hiddenFu} more)
          </button>
        )}
        {showAllTasks && totalFollowups > 8 && (
          <button
            type="button"
            onClick={() => setShowAllTasks(false)}
            className="block w-full px-4 py-2 text-center text-xs font-medium text-slate-400 transition-colors hover:text-slate-600"
          >
            Show fewer
          </button>
        )}
      </Card>
      <DispatchPeek dispatchId={peek} onClose={() => setPeek(null)} />
    </>
  );
}
